import type { Size } from "odyssey-shared";
import { WINDOW_LIMITS, type FrameBox, type PlaceRoom } from "../../ui/kit/place.ts";
import { HUD_SLOTS } from "../hud/slots.ts";
import { PANEL_INSET } from "./layout.ts";

/**
 * The float's layout rules (ADR-100's amendment of 2026-10-14, decision 49), pure: its default size and place, the
 * controls its strip can hold at a size, whether the Nearby legend fits under the field, and where the Places sheet
 * starts. The strip's rule is shared with the pop-out of P2.12-T18, whose small layout holds the same buttons.
 */

/**
 * The float's default size in logical pixels.
 */
export const FLOAT_SIZE: Size = Object.freeze({ width: 200, height: 200 });

/**
 * The outer band, in logical pixels, that fades to see-through and is the part of the box a resize starts from.
 */
export const FLOAT_RIM = 6;

/**
 * The control strip's height, and the side of each of its buttons.
 */
export const FLOAT_STRIP = 16;

/**
 * A strip button's side in logical pixels.
 */
export const STRIP_BUTTON = 16;

const STRIP_GAP = 2;
const STRIP_PAD = 2;
const STRIP_GRAB = 12;
const LEGEND_FIELD_LEAST = 96;

/**
 * A control of the float's strip. Pop out (P2.12-T18) sits between Dock and Close.
 */
export type StripControl = "zoomOut" | "zoomIn" | "places" | "centre" | "clearPin" | "dock" | "popOut" | "close";

/**
 * Every control in the order the strip draws them, left to right.
 */
export const STRIP_ORDER: readonly StripControl[] = Object.freeze(["zoomOut", "zoomIn", "places", "centre", "clearPin", "dock", "popOut", "close"] as const);

/**
 * The controls by how much a small strip wants them, most wanted first: a float must always be shut, docked and zoomed, and can be popped out before it has its other controls.
 */
const STRIP_PRIORITY: readonly StripControl[] = Object.freeze(["close", "dock", "zoomOut", "zoomIn", "popOut", "places", "centre", "clearPin"] as const);

/**
 * The width a strip of a number of buttons takes, its padding included.
 *
 * @param count The number of buttons.
 * @returns Logical pixels; 0 for none.
 */
export function stripWidth(count: number): number {
  return count <= 0 ? 0 : 2 * STRIP_PAD + count * STRIP_BUTTON + (count - 1) * STRIP_GAP;
}

/**
 * The controls the strip holds at a size: of those the level has, as many as fit with room left to grab the strip by,
 * the least wanted dropped first, in the strip's order.
 *
 * @param size The room the strip has, its width the float's less its rim on each side.
 * @param available The controls the level and state offer.
 * @returns The controls to draw.
 */
export function stripControls(size: Size, available: readonly StripControl[]): readonly StripControl[] {
  const wanted = STRIP_PRIORITY.filter((control) => available.includes(control));
  let count = wanted.length;
  while (count > 0 && stripWidth(count) + STRIP_GRAB > size.width) {
    count -= 1;
  }
  const kept = wanted.slice(0, count);
  return STRIP_ORDER.filter((control) => kept.includes(control));
}

/**
 * Whether the Nearby legend is drawn under the field: only where the field keeps at least 96 logical pixels of height
 * above it.
 *
 * @param view The float's view size.
 * @param strip The strip the legend would take under the field, 0 for no legend.
 * @returns True when the legend fits.
 */
export function floatLegend(view: Size, strip: number): boolean {
  return strip > 0 && view.height - strip >= LEGEND_FIELD_LEAST;
}

/**
 * Where the Places sheet starts in the view: under the control strip and the view's inset.
 *
 * @returns Logical pixels from the view's top.
 */
export function floatPanelTop(): number {
  return FLOAT_STRIP + PANEL_INSET;
}

/**
 * The float's default box in a room: its default size, held inside the room and no smaller than the frame's least, at
 * the room's right edge and above the game menu, or the room's top where there is no height for that.
 *
 * @param room The room windows are held in.
 * @returns The box.
 */
export function floatHome(room: PlaceRoom): FrameBox {
  const { min } = WINDOW_LIMITS.mapFloat;
  const width = Math.max(min.width, Math.min(FLOAT_SIZE.width, room.right - room.left));
  const height = Math.max(min.height, Math.min(FLOAT_SIZE.height, room.bottom - room.top));
  const menu = HUD_SLOTS.menu.height + HUD_SLOTS.menu.y;
  return { left: Math.max(room.left, room.right - width), top: Math.max(room.top, room.bottom - menu - height), width, height };
}
